import {
  Directive,
  ElementRef,
  HostListener,
  inject,
  Input,
  OnDestroy,
  PLATFORM_ID,
  Renderer2,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

export type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

const SHOW_DELAY_MS = 150;
const VIEWPORT_GUTTER = 8;

/**
 * Lightweight, brand-styled tooltip directive — a reusable replacement for the
 * native `title` attribute so every module gets a tooltip that matches the
 * app's look (Inter font, dark card, brand shadow) instead of the OS default.
 *
 * Usage: <button [appTooltip]="'Available once the storybook is created'">…</button>
 * Optional: [tooltipPosition]="'bottom'" (default 'top'), [tooltipDisabled]="cond"
 */
@Directive({
  selector: '[appTooltip]',
  standalone: true,
})
export class TooltipDirective implements OnDestroy {
  @Input('appTooltip') text = '';
  @Input() tooltipPosition: TooltipPosition = 'top';
  @Input() tooltipDisabled = false;

  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly renderer = inject(Renderer2);
  private readonly platformId = inject(PLATFORM_ID);

  private tooltipEl: HTMLElement | null = null;
  private showTimeoutId: ReturnType<typeof setTimeout> | null = null;

  @HostListener('mouseenter')
  @HostListener('focusin')
  onShow(): void {
    if (!isPlatformBrowser(this.platformId) || this.tooltipDisabled || !this.text?.trim()) return;

    this.clearShowTimer();
    this.showTimeoutId = setTimeout(() => this.render(), SHOW_DELAY_MS);
  }

  @HostListener('mouseleave')
  @HostListener('focusout')
  @HostListener('click')
  onHide(): void {
    this.clearShowTimer();
    this.destroyTooltip();
  }

  ngOnDestroy(): void {
    this.clearShowTimer();
    this.destroyTooltip();
  }

  private clearShowTimer(): void {
    if (this.showTimeoutId !== null) {
      clearTimeout(this.showTimeoutId);
      this.showTimeoutId = null;
    }
  }

  private render(): void {
    this.destroyTooltip();

    const tooltip = this.renderer.createElement('div') as HTMLElement;
    this.renderer.addClass(tooltip, 'fm-tooltip');
    const textNode = this.renderer.createText(this.text);
    this.renderer.appendChild(tooltip, textNode);
    this.renderer.appendChild(document.body, tooltip);
    this.tooltipEl = tooltip;

    this.position(tooltip);

    // Next frame so the transition (opacity/transform) actually animates in.
    requestAnimationFrame(() => this.renderer.addClass(tooltip, 'fm-tooltip-visible'));
  }

  private position(tooltip: HTMLElement): void {
    const hostRect = this.host.nativeElement.getBoundingClientRect();
    const tipRect = tooltip.getBoundingClientRect();

    let placement = this.tooltipPosition;
    if (placement === 'top' && hostRect.top - tipRect.height < VIEWPORT_GUTTER) {
      placement = 'bottom';
    } else if (placement === 'bottom' && hostRect.bottom + tipRect.height > window.innerHeight - VIEWPORT_GUTTER) {
      placement = 'top';
    }
    this.renderer.addClass(tooltip, `fm-tooltip-${placement}`);

    let top = 0;
    let left = 0;

    switch (placement) {
      case 'bottom':
        top = hostRect.bottom + 8;
        left = hostRect.left + hostRect.width / 2 - tipRect.width / 2;
        break;
      case 'left':
        top = hostRect.top + hostRect.height / 2 - tipRect.height / 2;
        left = hostRect.left - tipRect.width - 8;
        break;
      case 'right':
        top = hostRect.top + hostRect.height / 2 - tipRect.height / 2;
        left = hostRect.right + 8;
        break;
      case 'top':
      default:
        top = hostRect.top - tipRect.height - 8;
        left = hostRect.left + hostRect.width / 2 - tipRect.width / 2;
        break;
    }

    left = Math.min(Math.max(left, VIEWPORT_GUTTER), window.innerWidth - tipRect.width - VIEWPORT_GUTTER);
    top = Math.min(Math.max(top, VIEWPORT_GUTTER), window.innerHeight - tipRect.height - VIEWPORT_GUTTER);

    this.renderer.setStyle(tooltip, 'top', `${top}px`);
    this.renderer.setStyle(tooltip, 'left', `${left}px`);
  }

  private destroyTooltip(): void {
    if (this.tooltipEl) {
      this.tooltipEl.remove();
      this.tooltipEl = null;
    }
  }
}
